import { observer } from 'mobx-react-lite'
import { Fragment, type ReactElement } from 'react'
import { useNewListWizard } from 'app/components/lists/new/useNewListWizard'
import { ListsSection } from 'app/components/sidebar/ListsSection'
import { ManageListsDialog } from 'app/components/sidebar/ManageListsDialog'
import { NavGroup } from 'app/components/sidebar/NavGroup'
import { NavRow } from 'app/components/sidebar/NavRow'
import { NavSectionHeader } from 'app/components/sidebar/NavSectionHeader'
import { SidebarChrome } from 'app/components/sidebar/SidebarChrome'
import { useDocumentTitle } from 'app/hooks/useDocumentTitle'
import { useHomepage } from 'app/hooks/useHomepage'
import { useNavGate } from 'app/hooks/useNavGate'
import { useNavInputs } from 'app/hooks/useNavInputs'
import { useNavLists } from 'app/hooks/useNavLists'
import { usePracticeLists } from 'app/hooks/usePracticeLists'
import { useSettings } from 'app/hooks/useSettings'
import { closeModal, showModal } from 'app/lib/modal'
import type { UserJson } from 'app/types/entities'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { useCustomPageCreated } from 'lib/customPageEvents'
import { getNavSections, navDocumentTitle } from 'lib/navSections'
import { filterNavSections } from 'lib/navVisibility'
import type { NavItemType } from 'types/Sidebar'
import { useCloseNav } from 'app/hooks/useCloseNav'

const COLLAPSIBLE_SECTIONS: ReadonlySet<string> = new Set([`records`, `lists`])
const COLLAPSED_SECTIONS_SETTING = `navCollapsedSections`

const GroupedNav = observer(function GroupedNav({ user }: { user: UserJson }): ReactElement {
  const practiceLists = usePracticeLists()
  const { input, customPagesLoading, refetchCustomPages, refetchPipelines, refreshSidebarCounts } = useNavInputs(
    user,
    practiceLists.lists,
  )
  const gate = useNavGate(user)
  // Pruning the saved order before the Lists are in would erase their positions too.
  const lists = useNavLists(input, gate, customPagesLoading || practiceLists.loading)
  const homepage = useHomepage()
  const [collapsedSections = [], saveCollapsedSections] = useSettings<string[]>(COLLAPSED_SECTIONS_SETTING)
  const toggleSection = (id: string) =>
    saveCollapsedSections(
      COLLAPSED_SECTIONS_SETTING,
      collapsedSections.includes(id)
        ? collapsedSections.filter((section) => section !== id)
        : [...collapsedSections, id],
    )
  const sections = gate ? filterNavSections(getNavSections(input), gate) : []
  useDocumentTitle(navDocumentTitle(sections, lists.entries))
  useCustomPageCreated(() => bg(refetchCustomPages))

  const refreshPipelines = () => {
    refetchPipelines()
    refreshSidebarCounts()
  }
  // Contact lists come from the List API; activity lists still come from custom pages, and
  // the old pipelines stay in step for the pages that read them.
  const refreshLists = async () => {
    await Promise.all([refetchCustomPages(), practiceLists.refetch()])
    refetchPipelines()
    refreshSidebarCounts()
  }
  const closeNav = useCloseNav()
  const wizard = useNewListWizard({
    recordTypes: lists.recordTypes,
    canCreateManualList: lists.canCreateManualList,
    onListsChanged: refreshLists,
  })
  const renderItem = (item: NavItemType) =>
    item.children && !item.rowOnly ? (
      <NavGroup
        key={item.name}
        item={item}
        homepage={homepage}
        pipelines={input.pipelines}
        onPipelinesChanged={refreshPipelines}
      />
    ) : (
      <NavRow key={item.name} item={item} homepage={homepage} />
    )
  const footer = sections.find((section) => section.id === `footer`)
  const body = sections.filter((section) => section.id !== `footer`)
  const listsSection = lists.entries.length > 0 && (
    <ListsSection
      key="lists"
      collapsed={collapsedSections.includes(`lists`)}
      onToggleCollapsed={() => toggleSection(`lists`)}
      lists={lists}
      homepage={homepage}
      onListsChanged={refreshLists}
      // On a phone the drawer closes first, so the dialog is not stacked on top of it.
      onNewList={() => {
        closeNav()
        wizard.open()
      }}
      onManage={() => {
        closeNav()
        showModal(
          <ManageListsDialog
            entries={lists.entries}
            setting={lists.setting}
            onSave={lists.save}
            onListsChanged={refreshLists}
            onAddFromTemplate={() => {
              closeModal()
              wizard.open()
            }}
          />,
          {
            title: `Manage lists`,
            isFullScreen: false,
            overflowHidden: false,
            useRadix: true,
            // The dialog pads itself, tighter on a phone.
            removePadding: true,
          },
        )
      }}
    />
  )
  // Lists sits above Records: the slices people work from come before the raw tables.
  const listsBeforeRecords = body.some((section) => section.id === `records`)

  return (
    <SidebarChrome userMenuItems={footer?.items ?? []}>
      {body.map((section) => {
        const collapsible = COLLAPSIBLE_SECTIONS.has(section.id)
        const collapsed = collapsible && collapsedSections.includes(section.id)
        // A collapsed section keeps the current page's row so the nav never loses where you are.
        const items = collapsed
          ? section.items.filter((item) => item.active || item.children?.some((child) => child.active))
          : section.items
        return (
          <Fragment key={section.id}>
            {section.id === `records` && listsSection}
            <section aria-label={section.label} className="flex flex-col">
              {section.label && (
                <NavSectionHeader
                  label={section.label}
                  collapsed={collapsed}
                  onToggle={collapsible ? () => toggleSection(section.id) : undefined}
                />
              )}
              {items.map(renderItem)}
            </section>
          </Fragment>
        )
      })}
      {!listsBeforeRecords && listsSection}
    </SidebarChrome>
  )
})

export const SideBarV3 = observer(function SideBarV3(): ReactElement {
  const user = useUser()
  return user ? <GroupedNav user={user} /> : <></>
})
